import React, { useState, useEffect } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';
import PricingProfileSelector from './PricingProfileSelector';
import ComparacaoPrecos from './ComparacaoPrecos';
import OrcamentoComparativo from './OrcamentoComparativo';

interface TabSistemaCotacaoProps {
  eventoData: {
    nomeEvento: string;
    tipoEvento: string;
    dataEvento: string;
    horarioInicio: string;
    horarioTermino: string;
    localEvento: string;
    numeroConvidados: number;
    nivelConsumo: string;
    drinksEscolhidos: Array<{
      id: string;
      name: string;
      quantity: number;
      is_standard: boolean;
    }>;
    bartenders: number;
    assistentes: number;
    complementos: {
      acucar: boolean;
      adocante: boolean;
      gelo: boolean;
      canudo: boolean;
      tipoCopos: string;
      balcao: boolean;
    };
    margemLucro: number;
  };
  setEventoData: React.Dispatch<React.SetStateAction<any>>;
}

const TabSistemaCotacao: React.FC<TabSistemaCotacaoProps> = ({ eventoData, setEventoData }) => {
  const [perfilPreco, setPerfilPreco] = useState<'standard' | 'premium' | 'both'>('both');
  const [selectedDrink, setSelectedDrink] = useState<string | null>(null);

  // Selecionar o primeiro drink para comparação individual
  useEffect(() => {
    if (eventoData.drinksEscolhidos.length > 0 && !selectedDrink) {
      setSelectedDrink(eventoData.drinksEscolhidos[0].id);
    }
  }, [eventoData.drinksEscolhidos, selectedDrink]);

  const handleProfileChange = (profile: 'standard' | 'premium' | 'both') => {
    setPerfilPreco(profile);
    
    // Atualizar o perfil de todos os drinks se não estiver no modo comparativo
    if (profile !== 'both') {
      const isStandard = profile === 'standard';
      setEventoData({
        ...eventoData,
        drinksEscolhidos: eventoData.drinksEscolhidos.map(drink => ({
          ...drink,
          is_standard: isStandard
        }))
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Seletor de Perfil de Preço */}
      <PricingProfileSelector 
        onProfileChange={handleProfileChange}
        initialProfile="both"
      />

      {/* Comparação de Preços para um Drink Específico */}
      {selectedDrink && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <DynamicIcon name="glasses" size={20} className="mr-2 text-blue-600" />
            Comparação por Drink
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selecione um drink para comparar
            </label>
            <select
              value={selectedDrink}
              onChange={(e) => setSelectedDrink(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              {eventoData.drinksEscolhidos.map(drink => (
                <option key={drink.id} value={drink.id}>
                  {drink.name}
                </option>
              ))}
            </select>
          </div>
          
          <ComparacaoPrecos 
            drinkId={selectedDrink} 
            quantidade={eventoData.drinksEscolhidos.find(d => d.id === selectedDrink)?.quantity || 1}
          />
        </div>
      )}

      {/* Orçamento Comparativo Completo */}
      {eventoData.drinksEscolhidos.length > 0 && (
        <OrcamentoComparativo 
          drinks={eventoData.drinksEscolhidos}
          eventoData={eventoData}
        />
      )}

      {/* Dicas e Recomendações */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-blue-800 flex items-center">
          <DynamicIcon name="info" size={20} className="mr-2 text-blue-600" />
          Dicas para Apresentação
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DynamicIcon name="lightBulb" size={16} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Ofereça Ambas as Opções</h4>
              <p className="text-blue-700 text-sm">
                Apresente as opções Standard e Premium lado a lado para que o cliente possa escolher 
                de acordo com seu orçamento e preferência.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DynamicIcon name="target" size={16} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Destaque o Valor Agregado</h4>
              <p className="text-blue-700 text-sm">
                Explique o que o cliente ganha ao optar pela versão Premium, como marcas reconhecidas, 
                qualidade superior e experiência diferenciada.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DynamicIcon name="dollarSign" size={16} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Flexibilidade no Orçamento</h4>
              <p className="text-blue-700 text-sm">
                Sugira opções mistas, com alguns drinks premium e outros standard, 
                para atender diferentes perfis de convidados e otimizar o orçamento.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabSistemaCotacao;